import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Typography } from 'antd';
import {
  DashboardOutlined,
  FlagOutlined,
  AppstoreOutlined,
  SettingOutlined,
  AuditOutlined,
  MonitorOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  HistoryOutlined,
  CloudUploadOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FlaggedCategorization from './FlaggedCategorization';
import TaxCategories from './TaxCategories';
import ExciseTaxRules from './TaxRules';
import SalesTaxRules from './SalesTaxRules';
import Jurisdictions from './Jurisdictions';
import TaxAudit from './TaxAudit';
import SystemActivityLogs from './SystemActivityLogs';
import BulkDataImport from './BulkDataImport';
import ExciseTaxSeed from './ExciseTaxSeed';
import './AdminDashboard.css';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

// Placeholder components for other admin features
const DashboardHome = () => (
  <div className="dashboard-placeholder">
    <Title level={2}>Admin Dashboard</Title>
    <p>Welcome to the Lithlan Admin Portal. Use the navigation menu to access admin tools.</p>
  </div>
);


const SystemMonitoring = () => (
  <div className="dashboard-placeholder">
    <Title level={2}>System Monitoring</Title>
    <p>System monitoring dashboard will be implemented here.</p>
  </div>
);

const SubscriptionPlans = () => (
  <div className="dashboard-placeholder">
    <Title level={2}>Subscription Plans [FUTURE ENHANCEMENT]</Title>
    <p>Subscription plan management will be implemented in a future release.</p>
  </div>
);

function AdminDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: user?.email,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/dashboard/flagged-categorization',
      icon: <FlagOutlined />,
      label: 'Flagged Products',
    },
    {
      key: 'tax-management',
      icon: <DollarOutlined />,
      label: 'Tax Management',
      type: 'group',
    },
    {
      key: '/dashboard/categories',
      icon: <AppstoreOutlined />,
      label: 'Tax Categories',
    },
    {
      key: '/dashboard/excise-rules',
      icon: <SettingOutlined />,
      label: 'Excise Tax Rules',
    },
    {
      key: '/dashboard/sales-tax-rules',
      icon: <GlobalOutlined />,
      label: 'Sales Tax Rules',
    },
    {
      key: '/dashboard/jurisdictions',
      icon: <EnvironmentOutlined />,
      label: 'Jurisdictions',
    },
    {
      key: 'system-management',
      icon: <MonitorOutlined />,
      label: 'System Management',
      type: 'group',
    },
    {
      key: '/dashboard/audit',
      icon: <AuditOutlined />,
      label: 'Tax Audit Logs',
    },
    {
      key: '/dashboard/system-activity',
      icon: <HistoryOutlined />,
      label: 'System Activity Logs',
    },
    {
      key: '/dashboard/bulk-import',
      icon: <CloudUploadOutlined />,
      label: 'Bulk Data Import',
    },
    {
      key: '/dashboard/excise-tax-seed',
      icon: <DatabaseOutlined />,
      label: 'Excise Tax Seed',
    },
    {
      key: '/dashboard/monitoring',
      icon: <MonitorOutlined />,
      label: 'System Monitoring',
    },
    {
      key: '/dashboard/subscriptions',
      icon: <UserOutlined />,
      label: 'Subscription Plans [FUTURE]',
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const getCurrentMenuKey = () => {
    const path = location.pathname;
    const menuItem = menuItems.find(item => item.key === path);
    return menuItem ? menuItem.key : '/dashboard';
  };

  return (
    <Layout className="admin-dashboard-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="admin-dashboard-sider"
        width={256}
      >
        <div className="admin-dashboard-logo">
          <Title level={collapsed ? 5 : 4} className="logo-text">
            {collapsed ? 'L' : 'Lithlan Admin'}
          </Title>
        </div>
        
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getCurrentMenuKey()]}
          items={menuItems}
          onClick={handleMenuClick}
          className="admin-dashboard-menu"
        />
      </Sider>
      
      <Layout className="admin-dashboard-content-layout">
        <Header className="admin-dashboard-header">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="collapse-button"
          />
          
          <div className="header-right">
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button type="text" className="user-button">
                <Avatar size="small" icon={<UserOutlined />} />
                <span className="user-email">{user?.email}</span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="admin-dashboard-content">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="flagged-categorization" element={<FlaggedCategorization />} />
            <Route path="categories" element={<TaxCategories />} />
            <Route path="excise-rules" element={<ExciseTaxRules />} />
            <Route path="sales-tax-rules" element={<SalesTaxRules />} />
            <Route path="jurisdictions" element={<Jurisdictions />} />
            <Route path="audit" element={<TaxAudit />} />
            <Route path="system-activity" element={<SystemActivityLogs />} />
            <Route path="bulk-import" element={<BulkDataImport />} />
            <Route path="excise-tax-seed" element={<ExciseTaxSeed />} />
            <Route path="monitoring" element={<SystemMonitoring />} />
            <Route path="subscriptions" element={<SubscriptionPlans />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export default AdminDashboard;